import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useSlug } from '@/hooks/useSlug'
import { type CustomerFormData, customerFormSchema } from '@/types/customers'

export const defaultCustomerFormValues: CustomerFormData = {
    name: '',
    identifier: '',
    notes: '',
    expand: {
        address: {
            line1: '',
            line2: '',
            city: '',
            stateProvinceCounty: '',
            zipOrPostcode: '',
            country: '',
        },
    },
}

export const customerFormConfig = {
    mode: 'onChange' as const,
    resolver: zodResolver(customerFormSchema),
}

interface UseCustomerFormOptions {
    existingCustomer?: CustomerFormData
    onSubmit?: (data: CustomerFormData) => void | Promise<void>
}

export function useCustomerForm({ existingCustomer, onSubmit }: UseCustomerFormOptions = {}) {
    const form = useForm<CustomerFormData>({
        ...customerFormConfig,
        defaultValues: defaultCustomerFormValues,
    })

    const { reset, watch, setValue, handleSubmit, formState } = form

    useSlug<CustomerFormData>({ watch, setValue })

    useEffect(() => {
        if (existingCustomer) {
            reset(existingCustomer)
        }
    }, [existingCustomer, reset])

    const handleFormSubmit = handleSubmit(async data => {
        if (onSubmit) {
            await onSubmit(data)
        }
    })

    return {
        ...form,
        handleSubmit: handleFormSubmit,
        isSubmitting: formState.isSubmitting,
    }
}
